import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { LanguageProvider, useLanguage } from './hooks/useLanguage';
import Header from './components/Header.jsx';
import Hero from './components/Hero.jsx';
import SocialProof from './components/SocialProof.jsx';
import OffersSection from './components/OffersSection.jsx';
import HowItWorks from './components/HowItWorks.jsx';
import SafetyTrust from './components/SafetyTrust.jsx';
import Gallery from './components/Gallery.jsx';
import Testimonials from './components/Testimonials.jsx';
import FAQ from './components/FAQ.jsx';
import Location from './components/Location.jsx';
import FinalCTA from './components/FinalCTA.jsx';
import Footer from './components/Footer.jsx';
import ChatWidget from './components/ChatWidget.jsx';
import StickyMobileCTA from './components/StickyMobileCTA.jsx';
import AdminPanel from './pages/AdminPanel.jsx';
import OfferDetailPage from './pages/OfferDetailPage.jsx';
import CookiePolicyPage from './pages/CookiePolicyPage.jsx';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.jsx';
import ContactPage from './pages/ContactPage.jsx';
import ContactConfirmationPage from './pages/ContactConfirmationPage.jsx';
import NewsletterConfirmationPage from './pages/NewsletterConfirmationPage.jsx';
import BookingConfirmationPage from './pages/BookingConfirmationPage.jsx';
import CookieConsent from './components/CookieConsent.jsx';
import PromoBar from './components/PromoBar.jsx';
import MatomoAnalytics from './components/MatomoAnalytics.jsx';
import MatomoConfigLoader from './components/MatomoConfigLoader.jsx';
import GoogleAnalytics from './components/GoogleAnalytics.jsx';
import GoogleAnalyticsConfigLoader from './components/GoogleAnalyticsConfigLoader.jsx';
import SeoHead from './components/SeoHead.jsx';
import { getFaqItems } from './lib/faqContent';
import { buildHomepageSchemas } from './lib/seoSchema';
import './styles/cookieconsent-custom.css';
import { Toaster } from "./components/ui/sonner"

function LandingPage() {
  const [searchFilters, setSearchFilters] = useState(null);
  const [reviewStats, setReviewStats] = useState({ rating: 5.0, review_count: 0 });
  const { t } = useLanguage();
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const reviewsRes = await axios.get(`${API_URL}/api/google-reviews-stats`);
        if (cancelled) return;
        setReviewStats(reviewsRes.data || { rating: 5.0, review_count: 0 });
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_URL]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const faqItems = getFaqItems(t);
  const homepageSchemas = origin ? buildHomepageSchemas(origin, faqItems, reviewStats) : [];

  return (
    <div className="font-sans antialiased text-gray-900 bg-white selection:bg-accent-200 selection:text-primary-950">
      <SeoHead
        title={`${t('hero.headline')} | Wownautic`}
        description={t('hero.subheadline')}
        canonicalPath="/"
        ogType="website"
        jsonLd={homepageSchemas}
      />
      <Header />
      <main>
        <Hero onSearch={setSearchFilters} />
        <SocialProof />
        <OffersSection searchFilters={searchFilters} />
        <HowItWorks />
        <SafetyTrust />
        <Gallery />
        <Testimonials />
        <FAQ />
        <Location />
        <FinalCTA />
      </main>
      <Footer />
      <ChatWidget />
      <StickyMobileCTA />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <Router>
        <PromoBar />
        <MatomoConfigLoader />
        <GoogleAnalyticsConfigLoader />
        <CookieConsent />
        <MatomoAnalytics />
        <GoogleAnalytics />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/offer/:id" element={<OfferDetailPage />} />
          <Route path="/:category/:slug" element={<OfferDetailPage />} />
          <Route path="/:slug" element={<OfferDetailPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/contact-confirmation" element={<ContactConfirmationPage />} />
          <Route path="/newsletter-confirmation" element={<NewsletterConfirmationPage />} />
          <Route path="/booking-confirmation" element={<BookingConfirmationPage />} />
          <Route path="/admin-secret-wownautic-2024" element={<AdminPanel />} />
        </Routes>
        <Toaster />
      </Router>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
